import {selectMessages} from '../../components/selectMessages'
import React, {useEffect, useRef, useState} from 'react'
import {EducationDatePicker} from '../../components/EducationDatePicker'
import Select from '../../components/EducationSelect'
import {IHomework, LANGUAGE_OPTIONS} from '../../constants'

// Send only fields accepted by the update endpoint; never mutate the loaded record.
export function homeworkUpdatePayload(homework: IHomework): IHomework {
  return {
    id: homework.id,
    storyId: homework.storyId,
    organizationId: homework.organizationId,
    schoolId: homework.schoolId,
    classroomId: homework.classroomId,
    startDate: new Date(homework.startDate),
    endDate: new Date(homework.endDate),
    languageCode: homework.languageCode || 'tr-TR',
    requestVoiceRecording: homework.requestVoiceRecording === true,
    isReadAloud: homework.isReadAloud === true && !homework.requestVoiceRecording,
  }
}

export function HomeworkSettingsForm({homework, onSave, onDelete}: {
  homework: IHomework
  onSave: (value: IHomework) => Promise<IHomework>
  onDelete?: () => void
}) {
  const [draft, setDraft] = useState(() => homeworkUpdatePayload(homework))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const busy = useRef(false)
  useEffect(() => {
    setDraft(homeworkUpdatePayload(homework))
    setError('')
  }, [homework])
  const dirty = JSON.stringify(homeworkUpdatePayload(draft)) !== JSON.stringify(homeworkUpdatePayload(homework))
  const setField = <K extends keyof IHomework>(key: K, value: IHomework[K]) => {
    setDraft(current => ({...current, [key]: value}))
    setError('')
  }
  const save = async (event: React.FormEvent) => {
    event.preventDefault()
    if (busy.current || !dirty) return
    if (!Number.isFinite(new Date(draft.startDate).getTime()) || !Number.isFinite(new Date(draft.endDate).getTime())) {
      setError('Lütfen geçerli bir başlangıç ve bitiş tarihi seçin.')
      return
    }
    if (new Date(draft.endDate) < new Date(draft.startDate)) {
      setError('Bitiş tarihi başlangıç tarihinden önce olamaz.')
      return
    }
    busy.current = true
    setSaving(true)
    setError('')
    try {
      await onSave(homeworkUpdatePayload(draft))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Değişiklikler kaydedilemedi. Lütfen tekrar deneyin.')
    } finally {
      busy.current = false
      setSaving(false)
    }
  }

  return (
    <form className='homework-settings-panel' onSubmit={save} aria-labelledby='homework-settings-title'>
      <div className='homework-panel-heading'>
        <div>
          <h2 id='homework-settings-title'>Sorumluluk ayarları</h2>
          <p>Tarihleri ve öğrencilerin okuma seçeneklerini düzenleyin.</p>
        </div>
      </div>
      <fieldset disabled={saving}>
        <div className='homework-settings-dates'>
          <div className='homework-settings-field'>
            <label htmlFor='detailStartDate'>Başlangıç tarihi</label>
            <EducationDatePicker id='detailStartDate' maxDate={new Date(draft.endDate)}
              selected={new Date(draft.startDate)} onChange={(date: Date | null) => date && setField('startDate', date)}
              disabled={saving} />
          </div>
          <div className='homework-settings-field'>
            <label htmlFor='detailEndDate'>Bitiş tarihi</label>
            <EducationDatePicker id='detailEndDate' minDate={new Date(draft.startDate)}
              selected={new Date(draft.endDate)} onChange={(date: Date | null) => date && setField('endDate', date)}
              disabled={saving} />
          </div>
        </div>
        <div className='homework-settings-field'>
          <label htmlFor='detailLanguage'>Kitap dili</label>
          <Select {...selectMessages} inputId='detailLanguage' className='kidly-select' classNamePrefix='kidly'
            options={LANGUAGE_OPTIONS} isDisabled={saving}
            value={LANGUAGE_OPTIONS.find(option => option.value === draft.languageCode)}
            onChange={option => option && setField('languageCode', option.value)} />
        </div>
        <div className='homework-settings-options'>
          <label className='homework-setting-switch' htmlFor='detailReadAloud'>
            <span><strong>Sesli dinleme</strong><small>{draft.requestVoiceRecording
              ? 'Ses kaydı açıkken kapalı.'
              : 'Kitabı sesli dinleyebilsin.'}</small></span>
            <input id='detailReadAloud' type='checkbox' role='switch'
              checked={draft.isReadAloud && !draft.requestVoiceRecording}
              disabled={saving || draft.requestVoiceRecording}
              onChange={event => setField('isReadAloud', event.target.checked)} />
            <span className='homework-setting-track' aria-hidden='true' />
          </label>
          <label className='homework-setting-switch' htmlFor='detailRequestRecording'>
            <span><strong>Ses kaydı istensin mi?</strong><small>Öğrenci okumasını kaydedip göndersin.</small></span>
            <input id='detailRequestRecording' type='checkbox' role='switch'
              checked={draft.requestVoiceRecording} disabled={saving}
              onChange={event => {
                const checked = event.target.checked
                setDraft(current => ({...current, requestVoiceRecording: checked, isReadAloud: checked ? false : current.isReadAloud}))
                setError('')
              }} />
            <span className='homework-setting-track' aria-hidden='true' />
          </label>
        </div>
      </fieldset>
      {error && <div role='alert' className='homework-settings-error'>{error}</div>}
      <div className='homework-settings-footer'>
        <span role='status'>{saving ? 'Kaydediliyor…' : dirty ? 'Kaydedilmemiş değişiklikler var' : 'Ayarlar güncel'}</span>
        <div>
          {onDelete && <button type='button' className='btn btn-light-danger' disabled={saving} onClick={onDelete}>
            <i className='fa-regular fa-trash-can' aria-hidden='true' /> Sorumluluğu kaldır
          </button>}
          <button type='submit' className='btn btn-education' disabled={saving || !dirty}>
            {saving ? 'Kaydediliyor…' : 'Değişiklikleri kaydet'}
          </button>
        </div>
      </div>
    </form>
  )
}
